import Path from "node:path";
import fs from "node:fs";

import { FSWatcher, watch } from "chokidar";
import { debounce } from "lodash";

import { ObservableValueProducerBase } from "./ChainableValueProducer";

export class FileSystemEntriesProducer extends ObservableValueProducerBase<string> {
    private readonly pathToDir: string;
    private readonly masks: string[];
    private readonly fullPathMasks: string[];
    private watcher?: FSWatcher;
    private currDirWatcher?: FSWatcher;

    public constructor(pathToDir: string, masks: string[]) {
        super();
        this.pathToDir = pathToDir;
        this.masks = masks;
        this.fullPathMasks = this.masks.map(mask => Path.join(pathToDir, mask));
    }

    protected async beginListening(): Promise<void> {
        if (this.watcher != undefined && this.currDirWatcher != undefined) {
            return;
        }

        this.currDirWatcher = watch("./", {
            ignored: changedPath => this.ifPathExists(changedPath, this.shouldIgnorePathChanges, () => true),
            ignoreInitial: true,
            cwd: this.pathToDir,
            ignorePermissionErrors: false,
            usePolling: true,
        });

        this.currDirWatcher.on("add", async () => this.initMasksWatcher());

        await this.initMasksWatcher();
    }

    private ifPathExists<Return>(
        path: string,
        thenCb: (path: string) => Return,
        elseCb: (path: string) => Return
    ): Return {
        return fs.existsSync(path) ? thenCb(path) : elseCb(path);
    }

    private readonly shouldIgnorePathChanges = (changedPath: string): boolean => {
        const normalizedChangedPath = Path.normalize(changedPath);
        const changedPathStats = fs.statSync(normalizedChangedPath);
        const pathPointsToDir = changedPathStats.isDirectory();
        if (pathPointsToDir) {
            return false;
        }
        const unwatchedPathChanged = !this.isPathMatchingSomeMasks(normalizedChangedPath);
        return unwatchedPathChanged;
    };

    private isPathMatchingSomeMasks(path: string): boolean {
        return this.fullPathMasks.some(mask => {
            const pathMatchMask = Path.matchesGlob(path, mask);

            return pathMatchMask;
        });
    }

    private async initMasksWatcher() {
        await this.watcher?.close();

        this.watcher = watch(this.masks, {
            ignoreInitial: false,
            cwd: this.pathToDir,
            ignorePermissionErrors: false,
            usePolling: true,
        });

        const handleAnyFsEventDebounced = debounce(() => this.handleAnyFsEvent(), 500);

        this.watcher.on("all", handleAnyFsEventDebounced);
        this.watcher.on("error", handleAnyFsEventDebounced);
    }

    protected async stopListening(): Promise<void> {
        await this.watcher?.close();
    }

    protected async getCurrentValue(): Promise<string> {
        return this.pathToDir;
    }

    private async handleAnyFsEvent(): Promise<void> {
        await this.emitUpdate(this.pathToDir);
    }
}
